import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import {
  CollideEvent,
  CollideId,
  isOneHitCollider,
  isPowerCollider,
} from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { PiggyBankBlockEntity } from './PiggyBankBlockEntity';
import { PiggyBankBlockEffect } from './PiggyBankBlockEffect';
import { app } from '../../app';

export class PiggyBankBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: PiggyBankBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: PiggyBankBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    if (!this.canAttack(event)) return false;
    this.attack(event);
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    if (!this.canAttack(event)) return false;
    this.attack(event);
    return true;
  }

  private canAttack(event: CollideEvent) {
    const count = this.entity.c.blockPiggyBank.count;
    const maxCount = 2;
    if (count >= maxCount && !isPowerCollider(event.collider?.type)) {
      return false;
    }
    return true;
  }

  private async attack(event: CollideEvent) {
    const entity = this.entity;
    const component = entity.c.blockPiggyBank;
    const effect = new PiggyBankBlockEffect(this.scene);

    // If the collider is a one hit collider, destroy in a single attack
    if (isOneHitCollider(event.collider?.type)) {
      component.count = 1;
    }

    if (component.count <= 1) {
      app.sound.play('puzzle-door-destroy');
      effect.animateBreakParticles(entity);
      effect.animateBreakExplosion(entity);
      despawnBlockEntity(this.scene, entity);
    } else {
      app.sound.play('puzzle-door-poke-chain');
      component.count -= 1;
      this.scene?.events.publish({ id: 'poke' });
    }
  }
}
